import clearCache from './clearCache'
import { forceSync } from '../syncWorker'
import database from '../database'
import { recordError, recordEvent } from '../observability/events'

const QUEUE_STORAGE_KEY = 'sync_pending_queue'

interface ClearStorageResult {
  success: boolean
  reason?: string
  pendingCount?: number
}

/**
 * Limpa o storage local para logout.
 *
 * IMPORTANTE: Verifica se há dados pendentes antes de limpar.
 * Se offline com dados pendentes, bloqueia o logout para evitar perda de dados.
 *
 * @param syncStatus - Status atual do sync (0=idle, 1=syncing, 2=done)
 * @returns Promise com resultado da operação
 */
export default async function clearStorage(
  syncStatus: number,
): Promise<ClearStorageResult> {
  recordEvent({
    level: 'info',
    category: 'auth',
    action: 'logout_started',
    payload: { syncStatus },
  })

  // 1. Verificar dados pendentes (agora em IndexedDB)
  let pendingCount = 0
  try {
    const pendingQueue = await database.adapter.getLocal(QUEUE_STORAGE_KEY)
    const pendingItems = pendingQueue ? JSON.parse(pendingQueue) : []
    pendingCount = pendingItems.filter(
      (item: any) => item.status === 'pending' || item.status === 'in_progress',
    ).length
  } catch (e) {
    console.warn('[clearStorage] failed to read pending queue', e)
    recordError(e instanceof Error ? e : new Error(String(e)), {
      category: 'auth',
      action: 'logout_pending_check_failed',
    })
    pendingCount = -1
  }

  if (pendingCount !== 0) {
    if (!navigator.onLine) {
      recordEvent({
        level: 'warn',
        category: 'auth',
        action: 'logout_blocked_offline_with_pending',
        payload: { pendingCount: pendingCount === -1 ? null : pendingCount },
      })
      return {
        success: false,
        reason: 'OFFLINE_WITH_PENDING_DATA',
        pendingCount: pendingCount === -1 ? undefined : pendingCount,
      }
    }

    if (syncStatus !== 2) {
      try {
        await forceSync(false)
        await new Promise((resolve) => setTimeout(resolve, 30000))

        const stillPending = await database.adapter.getLocal(QUEUE_STORAGE_KEY)
        const stillPendingItems = stillPending ? JSON.parse(stillPending) : []
        const stillPendingCount = stillPendingItems.filter(
          (item: any) =>
            item.status === 'pending' || item.status === 'in_progress',
        ).length

        if (stillPendingCount > 0) {
          recordEvent({
            level: 'warn',
            category: 'auth',
            action: 'logout_blocked_sync_incomplete',
            payload: { stillPendingCount },
          })
          return {
            success: false,
            reason: 'SYNC_INCOMPLETE',
            pendingCount: stillPendingCount,
          }
        }
      } catch (e) {
        recordError(e instanceof Error ? e : new Error(String(e)), {
          category: 'auth',
          action: 'logout_pre_sync_failed',
          payload: { pendingCount },
        })
        return {
          success: false,
          reason: 'SYNC_FAILED',
          pendingCount,
        }
      }
    }
  }

  const deleteDB = (name: string): Promise<void> => {
    return new Promise((resolve) => {
      const request = indexedDB.deleteDatabase(name)
      request.onsuccess = () => resolve()
      request.onerror = () => {
        recordEvent({
          level: 'warn',
          category: 'auth',
          action: 'logout_db_delete_error',
          payload: { db: name },
        })
        resolve()
      }
      request.onblocked = () => {
        recordEvent({
          level: 'warn',
          category: 'auth',
          action: 'logout_db_delete_blocked',
          payload: { db: name },
        })
        resolve()
      }
    })
  }

  await Promise.all([deleteDB('loki.db'), deleteDB('WatermelonIDBChecker')])

  try {
    localStorage.clear()
  } catch (e) {
    console.warn('[clearStorage] failed to clear localStorage', e)
    recordError(e instanceof Error ? e : new Error(String(e)), {
      category: 'auth',
      action: 'logout_clear_localstorage_failed',
    })
  }

  await clearCache()

  recordEvent({
    level: 'info',
    category: 'auth',
    action: 'logout_completed',
  })
  return { success: true }
}
